import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {useQuery} from '@tanstack/react-query';
import {FlaskConical, Loader2, Users} from 'lucide-react';
import AdminService from '@/modules/admin/services/adminService';
import type {AdminPatientSearchResult, AdminPatientSearchRequest, UserSearchResult} from '@/shared/types/api';
import {formatBirthdate} from '@/shared/utils/formatBirthdate';
import AdminUserMergeModal from './AdminUserMergeModal';
import AdminSyntheticResultsModal from './AdminSyntheticResultsModal';
import {isSyntheticResultsEditorAvailable} from '../utils/adminEnvironment';

type Mode = 'global' | 'org';

type PatientSearchSeed = {
    firstName?: string | null;
    lastName?: string | null;
    birthday?: string | null;
    organizationName?: string | null;
    providerName?: string | null;
};

interface Props {
    open: boolean;
    mode: Mode;
    onClose: () => void;
    initialQuery?: PatientSearchSeed | null;
    initialSelection?: PatientSearchSeed | null;
    onSelectUser?: (user: AdminPatientSearchResult) => void;
}

const mapOrgSearchResults = (rows: UserSearchResult[]): AdminPatientSearchResult[] => {
    return rows.map((row) => ({
        id: row.id,
        firstName: row.firstName,
        lastName: row.lastName,
        birthday: row.birthday ?? null,
        primaryOrganization: null,
        primaryProvider: null,
        organizations: [],
        providers: [],
    }));
};

export const AdminPatientSearchModal: React.FC<Props> = ({
    open,
    mode,
    onClose,
    initialQuery,
    initialSelection,
    onSelectUser,
}) => {
    const firstNameInputRef = useRef<HTMLInputElement | null>(null);
    const [filters, setFilters] = useState<AdminPatientSearchRequest>({
        firstName: '',
        lastName: '',
    });
    const [debouncedFilters, setDebouncedFilters] = useState<AdminPatientSearchRequest>({
        firstName: '',
        lastName: '',
    });

    useEffect(() => {
        if (!open) return;
        setFilters({
            firstName: initialQuery?.firstName?.toString() ?? '',
            lastName: initialQuery?.lastName?.toString() ?? '',
        });
    }, [initialQuery, open]);

    useEffect(() => {
        if (!open) return;
        const id = window.setTimeout(() => {
            firstNameInputRef.current?.focus();
        }, 0);
        return () => window.clearTimeout(id);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const handle = window.setTimeout(() => {
            setDebouncedFilters(filters);
        }, 350);
        return () => window.clearTimeout(handle);
    }, [filters, open]);

    const hasQuery = useMemo(() => {
        return Boolean((debouncedFilters.firstName ?? '').trim() || (debouncedFilters.lastName ?? '').trim());
    }, [debouncedFilters.firstName, debouncedFilters.lastName]);

    const searchQuery = useQuery({
        queryKey: ['admin-patient-search', mode, debouncedFilters],
        enabled: open && hasQuery,
        queryFn: async () => {
            if (!hasQuery) return [];
            if (mode === 'global') {
                return AdminService.searchUsersGlobal({
                    firstName: debouncedFilters.firstName,
                    lastName: debouncedFilters.lastName,
                });
            }
            const results = await AdminService.searchUsers({
                firstName: debouncedFilters.firstName,
                lastName: debouncedFilters.lastName,
            });
            return mapOrgSearchResults(results);
        },
    });

    const results = useMemo(() => (searchQuery.data ?? []) as AdminPatientSearchResult[], [searchQuery.data]);

    // Merge selection (global mode only).
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [mergeOpen, setMergeOpen] = useState(false);
    const [testResultsPatient, setTestResultsPatient] = useState<AdminPatientSearchResult | null>(null);

    // Drop selections that are no longer in the result set (or when closed).
    useEffect(() => {
        if (!open) {
            setSelectedIds([]);
            return;
        }
        const visible = new Set(results.map((r) => r.id));
        setSelectedIds((prev) => prev.filter((id) => visible.has(id)));
    }, [open, results]);

    const toggleSelected = (id: number) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const showMerge = mode === 'global';
    const showTestResults = mode === 'global' && isSyntheticResultsEditorAvailable();

    return (
        <>
            <Transition appear show={open} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={onClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/30" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-3xl rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                    <div className="border-b border-mld-panel-border px-6 py-4">
                                        <Dialog.Title className="text-lg font-semibold text-mld-menu-text">
                                            Patient Search
                                        </Dialog.Title>
                                        <p className="text-xs text-mld-muted-text">
                                            Search by first and last name
                                            {mode === 'org' ? ' in this organization.' : '.'}
                                        </p>
                                    </div>

                                    <div className="space-y-4 px-6 py-5">
                                        {initialSelection?.firstName || initialSelection?.lastName ? (
                                            <div className="rounded-lg border border-mld-panel-border bg-[var(--mld-background)] px-4 py-3 text-sm text-mld-menu-text">
                                                <div className="text-xs uppercase tracking-[0.18em] text-mld-muted-text">
                                                    Selected Patient
                                                </div>
                                                <div className="mt-1 font-semibold">
                                                    {[initialSelection?.firstName, initialSelection?.lastName]
                                                        .filter(Boolean)
                                                        .join(' ') || 'Unknown'}
                                                </div>
                                                {initialSelection?.birthday ? (
                                                    <div className="text-xs text-mld-muted-text">
                                                        DOB: {formatBirthdate(initialSelection.birthday)}
                                                    </div>
                                                ) : null}
                                                {initialSelection?.organizationName ? (
                                                    <div className="text-xs text-mld-muted-text">
                                                        Organization: {initialSelection.organizationName}
                                                    </div>
                                                ) : null}
                                                {initialSelection?.providerName ? (
                                                    <div className="text-xs text-mld-muted-text">
                                                        Provider: {initialSelection.providerName}
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : null}

                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                    First Name
                                                </label>
                                                <input
                                                    type="text"
                                                    ref={firstNameInputRef}
                                                    value={filters.firstName ?? ''}
                                                    onChange={(event) =>
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            firstName: event.target.value,
                                                        }))
                                                    }
                                                    className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                    Last Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={filters.lastName ?? ''}
                                                    onChange={(event) =>
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            lastName: event.target.value,
                                                        }))
                                                    }
                                                    className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                                                />
                                            </div>
                                        </div>

                                        <div className="text-xs text-mld-muted-text">
                                            {searchQuery.isFetching ? (
                                                <span className="inline-flex items-center gap-2">
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                    Searching...
                                                </span>
                                            ) : hasQuery ? (
                                                <span>
                                                    {results.length} result{results.length === 1 ? '' : 's'}
                                                </span>
                                            ) : (
                                                <span>Start typing to search.</span>
                                            )}
                                        </div>

                                        {hasQuery ? (
                                            <div className="max-h-80 overflow-y-auto rounded-lg border border-mld-panel-border">
                                                <table className="min-w-full divide-y divide-mld-panel-border text-sm">
                                                    <thead className="bg-mld-menu-background text-left text-[11px] uppercase tracking-[0.15em] text-mld-muted-text">
                                                        <tr>
                                                            {showMerge ? <th className="w-10 px-4 py-2" /> : null}
                                                            <th className="px-4 py-2">Name</th>
                                                            <th className="px-4 py-2">Birthday</th>
                                                            {mode === 'global' ? (
                                                                <>
                                                                    <th className="px-4 py-2">Organizations</th>
                                                                    <th className="px-4 py-2">Providers</th>
                                                                    {showTestResults ? (
                                                                        <th className="px-4 py-2 text-right">
                                                                            Test results
                                                                        </th>
                                                                    ) : null}
                                                                </>
                                                            ) : null}
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-mld-panel-border bg-white">
                                                        {results.length === 0 ? (
                                                            <tr>
                                                                <td
                                                                    colSpan={
                                                                        (mode === 'global' ? 4 : 2) +
                                                                        (showMerge ? 1 : 0) +
                                                                        (showTestResults ? 1 : 0)
                                                                    }
                                                                    className="px-4 py-6 text-center text-sm text-mld-muted-text">
                                                                    No matches found.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            results.map((user) => {
                                                                const fullName = [user.firstName, user.lastName]
                                                                    .filter(Boolean)
                                                                    .join(' ');
                                                                const clickable = mode === 'global' && onSelectUser;
                                                                return (
                                                                    <tr
                                                                        key={user.id}
                                                                        onClick={() => {
                                                                            if (!clickable) return;
                                                                            onSelectUser?.(user);
                                                                        }}
                                                                        className={
                                                                            clickable
                                                                                ? 'cursor-pointer hover:bg-mld-menu-background'
                                                                                : ''
                                                                        }>
                                                                        {showMerge ? (
                                                                            <td
                                                                                className="px-4 py-3"
                                                                                onClick={(event) =>
                                                                                    event.stopPropagation()
                                                                                }>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    className="accent-mld-primary"
                                                                                    aria-label={`Select ${fullName} for merge`}
                                                                                    checked={selectedIds.includes(
                                                                                        user.id
                                                                                    )}
                                                                                    onChange={() =>
                                                                                        toggleSelected(user.id)
                                                                                    }
                                                                                />
                                                                            </td>
                                                                        ) : null}
                                                                        <td className="px-4 py-3 font-medium text-mld-menu-text">
                                                                            {fullName || 'Unknown'}
                                                                        </td>
                                                                        <td className="px-4 py-3 text-mld-muted-text">
                                                                            {formatBirthdate(user.birthday)}
                                                                        </td>
                                                                        {mode === 'global' ? (
                                                                            <>
                                                                                <td className="px-4 py-3">
                                                                                    <div className="flex flex-wrap gap-1">
                                                                                        {user.organizations.length === 0
                                                                                            ? '—'
                                                                                            : user.organizations.map(
                                                                                                  (org) => (
                                                                                                      <span
                                                                                                          key={org.id}
                                                                                                          className="whitespace-nowrap rounded-full border border-mld-panel-border bg-[var(--mld-background)] px-2 py-0.5 text-[11px] text-mld-text-secondary">
                                                                                                          {org.displayName ||
                                                                                                              org.name}
                                                                                                      </span>
                                                                                                  )
                                                                                              )}
                                                                                    </div>
                                                                                </td>
                                                                                <td className="px-4 py-3">
                                                                                    <div className="flex flex-wrap gap-1">
                                                                                        {user.providers.length === 0
                                                                                            ? '—'
                                                                                            : user.providers.map(
                                                                                                  (provider) => (
                                                                                                      <span
                                                                                                          key={
                                                                                                              provider.id
                                                                                                          }
                                                                                                          className="whitespace-nowrap rounded-full border border-mld-panel-border bg-white px-2 py-0.5 text-[11px] text-mld-text-secondary">
                                                                                                          {
                                                                                                              provider.name
                                                                                                          }
                                                                                                      </span>
                                                                                                  )
                                                                                              )}
                                                                                    </div>
                                                                                </td>
                                                                                {showTestResults ? (
                                                                                    <td
                                                                                        className="px-4 py-3 text-right"
                                                                                        onClick={(event) =>
                                                                                            event.stopPropagation()
                                                                                        }>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() =>
                                                                                                setTestResultsPatient(
                                                                                                    user
                                                                                                )
                                                                                            }
                                                                                            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-mld-panel-border px-2.5 py-1.5 text-xs font-semibold text-mld-text-secondary hover:border-mld-primary hover:text-mld-primary">
                                                                                            <FlaskConical className="h-3.5 w-3.5" />
                                                                                            Synthetic Test Results
                                                                                        </button>
                                                                                    </td>
                                                                                ) : null}
                                                                            </>
                                                                        ) : null}
                                                                    </tr>
                                                                );
                                                            })
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="flex items-center justify-between border-t border-mld-panel-border px-6 py-4">
                                        <div className="text-xs text-mld-muted-text">
                                            {showMerge && selectedIds.length > 0 ? (
                                                <span>
                                                    {selectedIds.length} selected
                                                    {selectedIds.length < 2 ? ' — pick at least 2 to merge' : ''}
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="flex gap-2">
                                            {showMerge ? (
                                                <button
                                                    type="button"
                                                    disabled={selectedIds.length < 2}
                                                    onClick={() => setMergeOpen(true)}
                                                    className="inline-flex items-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50">
                                                    <Users className="h-4 w-4" />
                                                    Merge
                                                    {selectedIds.length >= 2 ? ` ${selectedIds.length} records` : ''}
                                                </button>
                                            ) : null}
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                className="rounded-md border border-mld-panel-border px-4 py-2 text-sm font-semibold text-mld-text-secondary hover:bg-[var(--mld-background)]">
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
            {showMerge ? (
                <AdminUserMergeModal
                    open={mergeOpen}
                    userIds={selectedIds}
                    onClose={() => setMergeOpen(false)}
                    onMerged={() => {
                        setSelectedIds([]);
                        void searchQuery.refetch();
                    }}
                />
            ) : null}
            {showTestResults ? (
                <AdminSyntheticResultsModal
                    open={Boolean(testResultsPatient)}
                    patient={testResultsPatient}
                    onClose={() => setTestResultsPatient(null)}
                />
            ) : null}
        </>
    );
};

export default AdminPatientSearchModal;
